/**
 * Nouveautés — la liste du mardi.
 *
 * Ce qui vient d'arriver en rayon pour la première fois, avec ce qu'il faut
 * pour décider : une photo ou pas, maison ou pas, et deux avertissements qui
 * reviennent tout le temps — la fiche héritée de l'import de janvier (elle
 * atteint le rayon aujourd'hui mais elle n'est pas neuve) et la fiche qui
 * ressemble à une existante (doublon, ou revente qui devient maison).
 *
 * L'écran PROPOSE, il ne publie rien : c'est le geste qui annonce. Sur trente
 * candidats d'une semaine réelle, neuf venaient de l'import et quinze fiches
 * sur trois mois ont été créées puis désactivées en moins d'une semaine.
 * Aucune règle ne sait écarter ça ; quelqu'un qui a l'article en main, oui.
 *
 * Les articles SANS photo sont mis à part, en dernier : ce sont eux qui
 * décident si la vitrine de la semaine tient ou pas.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { logger } from '../utils/logger';
import ProductAutocomplete from '../components/ProductAutocomplete';

interface Candidat {
  id: string;
  name: string;
  unit?: string | null;
  rayon?: string | null;
  prix_cents: number | null;
  thumbnail_url?: string | null;
  first_shelf_at: string;
  a_photo: boolean;
  maison: boolean;
  heritee_import: boolean;
  ressemble_a?: string | null;
  deja_annonce: boolean;
  novelty_word?: string | null;
}

interface AnnonceEnCours {
  id: string;
  name: string;
  rayon?: string | null;
  prix_cents: number | null;
  thumbnail_url?: string | null;
  a_photo: boolean;
  actif: boolean;
  annonce_le: string;
  jusqua: string;
  novelty_word: string;
}

const jourCourt = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-BE', { weekday: 'short', day: 'numeric', month: 'short' });

export default function NouveautesPage() {
  const [candidats, setCandidats] = useState<Candidat[]>([]);
  /**
   * Les annonces en cours, lues à part.
   *
   * Les candidats ne montrent que les PREMIÈRES mises en rayon de la semaine :
   * un produit annoncé « De retour » — les radis de saison, en rayon depuis des
   * années — n'y figure pas. Sans cette liste, on pouvait l'annoncer et ne plus
   * jamais voir l'annonce, donc ne plus jamais la retirer (signalé le 10/09).
   */
  const [annoncesEnCours, setAnnoncesEnCours] = useState<AnnonceEnCours[]>([]);
  const [recherche, setRecherche] = useState('');
  const [loading, setLoading] = useState(true);
  const [enCours, setEnCours] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      setLoading(true);
      const [data, actives] = await Promise.all([
        api.get<Candidat[]>('/products/novelty-candidates?jours=7'),
        api.get<AnnonceEnCours[]>('/products/novelty-active'),
      ]);
      setCandidats(data || []);
      setAnnoncesEnCours(actives || []);
    } catch (err) {
      logger.error('Erreur chargement des candidats nouveautés:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const annoncerParId = async (productId: string, kind: 'NEW' | 'BACK') => {
    try {
      setEnCours(productId);
      await api.patch(`/products/${productId}/novelty`, { kind });
      await charger();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Échec de l'annonce");
    } finally {
      setEnCours(null);
    }
  };

  const annoncer = async (c: Candidat, kind: 'NEW' | 'BACK') => {
    try {
      setEnCours(c.id);
      await api.patch(`/products/${c.id}/novelty`, { kind });
      await charger();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Échec de l'annonce");
    } finally {
      setEnCours(null);
    }
  };

  const retirer = async (c: { id: string }) => {
    try {
      setEnCours(c.id);
      await api.delete(`/products/${c.id}/novelty`);
      await charger();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Échec du retrait');
    } finally {
      setEnCours(null);
    }
  };

  // Trois piles, dans l'ordre où on les regarde : ce qui vient de chez nous,
  // ce qui est présentable, et ce qu'il faut photographier avant jeudi 14 h.
  const maison = candidats.filter((c) => c.maison && c.a_photo);
  const avecPhoto = candidats.filter((c) => !c.maison && c.a_photo);
  const sansPhoto = candidats.filter((c) => !c.a_photo);
  const annonces = candidats.filter((c) => c.deja_annonce).length;

  const Carte = ({ c }: { c: Candidat }) => (
    <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200">
      {c.thumbnail_url ? (
        <img
          src={c.thumbnail_url}
          alt=""
          className="w-14 h-14 rounded object-cover border border-gray-200 flex-none"
        />
      ) : (
        <div className="w-14 h-14 rounded bg-amber-50 border border-amber-300 flex-none flex items-center justify-center text-xl">
          📷
        </div>
      )}

      <div className="min-w-0 flex-1">
        <p className="font-semibold text-gray-900 leading-tight break-words">{c.name}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          {c.rayon || 'Sans rayon'} · en rayon depuis {jourCourt(c.first_shelf_at)}
          {c.prix_cents != null ? ` · ${(c.prix_cents / 100).toFixed(2)} €` : ''}
          {c.maison ? ' · notre production' : ''}
        </p>

        {(c.heritee_import || c.ressemble_a) && (
          <div className="mt-1.5 flex flex-col gap-1">
            {c.heritee_import && (
              <p className="text-xs text-amber-700">
                Fiche créée avant mars — elle arrive en rayon aujourd'hui, mais elle n'est pas neuve.
              </p>
            )}
            {c.ressemble_a && (
              <p className="text-xs text-amber-700">
                Ressemble à « {c.ressemble_a} » — doublon, ou revente qui devient maison ?
              </p>
            )}
          </div>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {c.deja_annonce ? (
            <>
              <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-300 text-xs font-semibold">
                ✨ {c.novelty_word}
              </span>
              <button
                onClick={() => void retirer(c)}
                disabled={enCours === c.id}
                className="px-2.5 py-1 rounded text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40"
              >
                retirer
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => void annoncer(c, 'NEW')}
                disabled={enCours === c.id}
                className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 min-h-[40px]"
              >
                {enCours === c.id ? '…' : '✨ Nouveau'}
              </button>
              <button
                onClick={() => void annoncer(c, 'BACK')}
                disabled={enCours === c.id}
                className="px-3 py-2 rounded-lg border border-emerald-600 text-emerald-800 text-sm font-semibold hover:bg-emerald-50 disabled:opacity-40 min-h-[40px]"
              >
                De retour
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const Pile = ({
    titre,
    detail,
    items,
  }: {
    titre: string;
    detail: string;
    items: Candidat[];
  }) =>
    items.length === 0 ? null : (
      <section className="mb-8">
        <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500">
          {titre} <span className="text-gray-400">({items.length})</span>
        </h2>
        <p className="text-xs text-gray-500 mb-3">{detail}</p>
        <div className="flex flex-col gap-2">
          {items.map((c) => (
            <Carte key={c.id} c={c} />
          ))}
        </div>
      </section>
    );

  return (
    <div className="p-4 md:p-6 max-w-3xl">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Nouveautés de la semaine</h1>
        <p className="text-sm text-gray-600 mt-1">
          Ce qui est arrivé en rayon pour la première fois ces sept derniers jours.{' '}
          {candidats.length > 0 && (
            <>
              <strong>{annonces}</strong> annoncé{annonces > 1 ? 's' : ''} sur {candidats.length}.
            </>
          )}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Cet écran ne publie rien : c'est l'annonce qui met le mot en boutique, et elle s'éteint
          seule à l'ouverture suivante.
        </p>
      </header>

      {/* Annoncer n'importe quel article, pas seulement ce qui vient d'arriver.
          C'est ici qu'on marque le retour de saison : aucune machine ne sait le
          voir (30 jours d'absence désignent 47 fiches d'épicerie jamais sorties
          du rayon contre 4 de fruits), et l'article n'est pas une première mise
          en rayon, donc il n'est pas dans les candidats. */}
      <section className="mb-8 p-4 rounded-lg border border-gray-200 bg-gray-50">
        <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-1">
          Annoncer un autre article
        </h2>
        <p className="text-xs text-gray-500 mb-3">
          Pour un produit déjà connu — un retour de saison, un article qu'on remet en avant.
        </p>
        <ProductAutocomplete
          value={recherche}
          onChange={setRecherche}
          onProductSelect={(p) => {
            setRecherche('');
            void annoncerParId(p.id, 'BACK');
          }}
          placeholder="Chercher un produit à annoncer « De retour »…"
        />
        <p className="text-xs text-gray-400 mt-2">
          Choisir un produit l'annonce « De retour ». Pour « Nouveau », passe par la liste ci-dessous
          ou par la mise en rayon.
        </p>
      </section>

      {annoncesEnCours.length > 0 && (
        <section className="mb-8">
          <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500">
            Annonces en cours <span className="text-gray-400">({annoncesEnCours.length})</span>
          </h2>
          <p className="text-xs text-gray-500 mb-3">
            Elles s'éteignent seules à l'ouverture de la boutique. Rien à faire — sauf si l'une
            d'elles n'a plus lieu d'être.
          </p>
          <div className="flex flex-col gap-2">
            {annoncesEnCours.map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-3 p-3 bg-white rounded-lg border border-emerald-200"
              >
                {a.thumbnail_url ? (
                  <img
                    src={a.thumbnail_url}
                    alt=""
                    className="w-10 h-10 rounded object-cover border border-gray-200 flex-none"
                  />
                ) : (
                  <div className="w-10 h-10 rounded bg-amber-50 border border-amber-300 flex-none flex items-center justify-center text-sm">
                    📷
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 truncate">
                    {a.name}
                    {!a.actif && <span className="ml-2 text-xs text-red-600">(fiche inactive)</span>}
                  </p>
                  <p className="text-xs text-gray-500">
                    ✨ {a.novelty_word} · jusqu'au {jourCourt(a.jusqua)}
                    {!a.a_photo ? ' · sans photo : n’entrera pas dans la note' : ''}
                  </p>
                </div>
                <button
                  onClick={() => void retirer(a)}
                  disabled={enCours === a.id}
                  className="px-2.5 py-1.5 rounded text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                >
                  retirer
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {loading ? (
        <p className="text-gray-500">Chargement…</p>
      ) : candidats.length === 0 ? (
        <p className="text-gray-500">
          Rien n'est arrivé en rayon cette semaine — ou les mises en rayon sont passées par un autre
          chemin que le transfert depuis la réserve.
        </p>
      ) : (
        <>
          <Pile
            titre="Notre production"
            detail="Ce qui sort de nos cultures : c'est ce que les clients viennent chercher."
            items={maison}
          />
          <Pile
            titre="Prêts à annoncer"
            detail="Photo présente : ils peuvent partir en vitrine et dans la note du jeudi."
            items={avecPhoto}
          />
          <Pile
            titre="À photographier"
            detail="Sans photo, un article ne va pas dans la note (une tuile grise dans un email ne donne envie de rien). L'annoncer reste possible, mais la photo est à faire avant jeudi 14 h."
            items={sansPhoto}
          />
        </>
      )}
    </div>
  );
}
